<template>
	<aside id="dashboard_sidebar"
			 class="left-0 top-0 z-9999 flex h-screen w-22.5 lg:w-70 flex-col overflow-y-hidden bg-primary duration-300 ease-linear transition-[left] static"
			 ref="target"><!-- translate-x-0-->
		<div class="flex items-center justify-between my-8 mx-auto mb-10.5">
			<router-link :to="{ name: 'dashboard' }">
				<img :src="logoLargePath" alt="Logo" title="Application Logo" class="hidden lg:block w-52" />
				<img :src="logoSmallPath" alt="Logo" title="Application Logo" class="lg:hidden w-13" />
			</router-link>
		</div>

		<div class="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear lg:px-5">
			<nav>
				<!-- User menu items - Route is checked against Tenant disabledRoutes array in SidebarItem -->
				<div>
					<ul class="flex flex-col gap-4 mb-2 lg:gap-2">
						<!-- HOME / DASHBOARD -->
						<SidebarItem :item="new SidebarMenuItem('dashboard/white/home.svg', 'Home', { name: 'dashboard' })" />

						<!-- RESOURCES -->
						<SidebarItem :item="new SidebarMenuItem('dashboard/white/resources.svg', 'Resources', { name: 'resources'})" />

						<!-- DATA SUBMISSIONS / MY SUBMISSIONS -->
						<SidebarItem v-if="(authStore.canUploadData || authStore.isPortalAdmin)" :item="new SidebarMenuItem('dashboard/white/submit-data.svg', 'Submit Data', { name: 'data.submit-data'})" />
						<!-- 	If a user can't upload, then there's nothing for them to see -->
						<SidebarItem v-if="(authStore.canViewAllOrgFiles || authStore.canUploadData || authStore.canDownloadData || authStore.isPortalAdmin)" :item="new SidebarMenuItem('dashboard/white/data-submissions.svg', ((authStore.isAnyAdmin || authStore.canViewAllOrgFiles) ? 'Data' : 'My') + ' Submissions', { name: 'data.data-submissions'})" />

						<!-- DOCUMENTS -->
						<SidebarItem :item="new SidebarMenuItem('dashboard/white/file.svg', 'Documents', '', [
							new SidebarMenuItem('', 'Manage', { name: 'documents.manage' }, null, !((authStore.canUploadDocuments || authStore.isPortalAdmin))),
							new SidebarMenuItem('', 'Published', { name: 'documents.published' }, null, !((authStore.canDownloadDocuments || authStore.isPortalAdmin)))
						 ])" />

						<!-- LEARNING CENTER -->
						<SidebarItem :item="new SidebarMenuItem('dashboard/white/learning-center.svg', 'Learning Center', { name: 'learning-center'})" />

					</ul>
				</div>

				<!-- Admin menu items -->
				<ul v-if="authStore.isPortalAdmin || authStore.isOrganizationAdmin" class="mb-6 flex flex-col gap-4 lg:gap-2">
					<SidebarItem :item="new SidebarMenuItem('dashboard/white/admin-center.svg', 'Admin Center', '', [
						new SidebarMenuItem('', 'Super Admin', { name: 'admin.super-admin' }, null, !authStore.isDeveloper),
						new SidebarMenuItem('', 'Portal Settings', { name: 'admin.portal-settings' }, null, !authStore.isPortalAdmin),
						new SidebarMenuItem('', 'Users', { name: 'admin.users' }),
						new SidebarMenuItem('', 'User Invites', { name: 'admin.user-invites' }),
						new SidebarMenuItem('', 'Organizations', { name: 'admin.organizations' }),
						new SidebarMenuItem('', 'Roles', { name: 'admin.organizations.roles' }, [
							new SidebarMenuItem('', 'Edit', { name: 'admin.organizations.roles.edit' }, null, showMenuItem('admin.organizations.roles.edit'))
						], !(authStore.isAnyAdmin)),
						new SidebarMenuItem('', 'Organization Groups', { name: 'admin.organization-groups' }, null, !(authStore.isPortalAdmin)),
						new SidebarMenuItem('', 'Data Submissions', { name: 'admin.data-submissions' }, null, !(authStore.isPortalAdmin)),
						new SidebarMenuItem('', 'Documents', { name: 'admin.documents' }, null, !(authStore.isPortalAdmin)),
						new SidebarMenuItem('', 'Resources', { name: 'admin.resources' }, null, !(authStore.isPortalAdmin)),
						new SidebarMenuItem('', 'Reports', { name: 'admin.reports.scheduled-reports' }, null, !(authStore.isPortalAdmin)),
						new SidebarMenuItem('', 'Support Messages', { name: 'admin.support.messages' }, null, !authStore.canManageSupport)
					 ])" />
				</ul>

				<!-- Debug menu items -->
				<ul v-if="devEnvEh" class="mb-6 flex flex-col gap-4 lg:gap-2">
					<SidebarItem :item="new SidebarMenuItem('debug.svg', 'Debug', '', [
						new SidebarMenuItem('', 'Styles Preview', { name: 'admin.styles' }),
					 ])" />
				</ul>
			</nav>
		</div>
	</aside>
</template>

<script setup lang="js">
import { computed, ref } from 'vue'
import SidebarItem from '@/components/Dashboard/Sidebar/SidebarItem.vue'
import { useAuthStore } from '@/stores/authStore.js'
import { SidebarMenuItem } from '@/models/SidebarMenuItem.js'
import { Tenants } from '@/models/tenants/Tenants.js'
import router from '@/router/router.js'

const authStore = useAuthStore()
const target = ref(null)
const tenantInfo = ref(authStore.getAuth ? authStore.getAuth?.tenantInfo() : { id: 0 })


const logoSmallPath = computed(() => {
	if (tenantInfo.value.id === 0) { return ''}
	const tenant = Tenants.getTenantById(tenantInfo.value.id)
	return new URL(`../../../assets/images/theme/${tenant.sidebarLogoSmall.image}`, import.meta.url).href;
});
const logoLargePath = computed(() => {
	if (tenantInfo.value.id === 0) { return ''}
	const tenant = Tenants.getTenantById(tenantInfo.value.id)
	return new URL(`../../../assets/images/theme/${tenant.sidebarLogoLarge.image}`, import.meta.url).href;
});

// eslint-disable-next-line no-undef
const devEnvEh = Tenants.isDevHost() && authStore.isPortalAdmin;//Prevent showing debug menu item if in production

/**
 * Used to show menu items only when on a specific page (one that a user typically can't navigate to)
 * @param p_route_name Route name
 * @returns {boolean}
 */
function showMenuItem(p_route_name)
{
	return router.currentRoute.value.name !== p_route_name
}
</script>
